import React, { useState } from "react";

import {
  Box,
  Link,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Container,
} from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";

import { useLocation, useNavigate } from "react-router-dom";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import BASE_URL from "../../config/apiConfig";
import { saveUserToken } from "../../utils/auth";

import "./UserLogin.css";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function UserLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  /**
   * Get redirect URL from:
   * 1. URL query: /user/login?redirect=/user/trial-add-farm
   * 2. React Router state: { redirectTo: "/user/trial-add-farm" }
   * 3. Default: /user/home
   */
  const getRedirectPath = () => {
    const params = new URLSearchParams(location.search);
    const queryRedirect = params.get("redirect");

    const stateRedirect = location.state?.redirectTo;

    const redirectPath = queryRedirect || stateRedirect;

    // Only allow internal routes
    if (
      redirectPath &&
      redirectPath.startsWith("/") &&
      !redirectPath.startsWith("//")
    ) {
      return redirectPath;
    }

    return "/user/home";
  };

  const handleSignIn = async (e) => {
    e.preventDefault();

    setLoading(true);
    setEmailError("");
    setPasswordError("");

    if (!emailRegex.test(email.trim())) {
      setEmailError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/user/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("Login success:", data);

        /**
         * Save authentication token
         */
        saveUserToken(data.token, data.user.email);

        /**
         * Redirect user to the page they originally wanted.
         *
         * Example:
         * /user/login?redirect=/user/trial-add-farm
         *
         * After login:
         * /user/trial-add-farm
         */
        const redirectPath = getRedirectPath();

        navigate(redirectPath, {
          replace: true,
        });
      } else {
        setEmailError(
          data?.message || "Invalid email or password. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error during login:", error);

      setPasswordError(
        "Something went wrong, please check your connection and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="farmology-user-auth-page">
      <Container className="farmology-user-auth-container">
        <Box className="farmology-user-auth-card">
          <Box className="farmology-user-form-panel">
            <Box className="farmology-user-brand">
              <Box className="farmology-user-logo-wrap">
                <img
                  src="/LOGOS/Farmology-Logo-e1687720251448.png"
                  alt="Farmology"
                  className="farmology-user-logo"
                />
              </Box>

              <Typography className="farmology-user-eyebrow">
                SMART AGRICULTURE
              </Typography>

              <Typography component="h1" className="farmology-user-title">
                Welcome back
              </Typography>

              <Typography className="farmology-user-description">
                Sign in to your Farmology account and continue making better
                decisions for every hectare.
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSignIn}
              className="farmology-user-form"
            >
              <TextField
                fullWidth
                name="email"
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError || ""}
                variant="outlined"
                className="farmology-user-field"
              />

              <TextField
                fullWidth
                name="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                error={!!passwordError}
                helperText={passwordError || ""}
                variant="outlined"
                className="farmology-user-field"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        className="farmology-user-password-toggle"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box className="farmology-user-forgot-row">
                <Link
                  href="/recoverpassword"
                  className="farmology-user-forgot-link"
                >
                  Forgot password?
                </Link>
              </Box>

              {(emailError || passwordError) && (
                <Typography
                  className="farmology-user-form-error"
                  aria-live="polite"
                >
                  {emailError || passwordError}
                </Typography>
              )}

              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={loading}
                className="farmology-user-submit"
              >
                Sign In
                <span className="farmology-user-arrow">↗</span>
              </LoadingButton>

              <Typography className="farmology-user-signup">
                Don’t have an account?{" "}
                <Link href="/signup" className="farmology-user-link">
                  Sign Up
                </Link>
              </Typography>
            </Box>

            <Typography className="farmology-user-footer-note">
              © {new Date().getFullYear()} Farmology. Smart decisions for
              smarter farming.
            </Typography>
          </Box>

          <Box className="farmology-user-visual-panel">
            <Box className="farmology-user-visual-copy">
              <Typography className="farmology-user-visual-eyebrow">
                FARMOLOGY PLATFORM
              </Typography>

              <Typography
                component="h2"
                className="farmology-user-visual-title"
              >
                Better data.
                <br />
                Better farming.
              </Typography>

              <Typography className="farmology-user-visual-text">
                Satellite intelligence, weather insights and practical farm
                information — together in one place.
              </Typography>
            </Box>

            <Box className="farmology-user-image-wrap">
              <img
                src="/Images/Login_Body.webp"
                alt="Farmology agriculture illustration"
                className="farmology-user-image"
              />
            </Box>

            <Box className="farmology-user-stat-card">
              <span className="farmology-user-stat-label">
                FARM INTELLIGENCE
              </span>

              <strong>Built around your farm</strong>
            </Box>

            <Box className="farmology-user-circle" />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default UserLogin;
